import type { ProductCode } from '../../branding';

/**
 * The two store apps, and whether each listing is public yet.
 *
 * Mirrors `svdy-website/src/data/apps.ts` and `svdy-authorization/app/core/branding.py`
 * (STORE_APPS). Flip `live` in all three on launch day; nothing else changes.
 *
 * One flag PER STORE: the stores do not approve on the same day. PureOA went live on
 * Google Play on 2026-11-02 while its first App Store release was still in review.
 * Measured, not assumed: QuTime iTunes lookup resultCount 1 + Play page live; PureOA Play
 * page live ("PureOA - Apps on Google Play"), iTunes lookup resultCount 0.
 *
 * The Apple URL must be the canonical `/app/id<numeric>` form (appStoreLinks.test.ts).
 * The ids are permanent and come from `svdy-mobile/eas.json` (`submit.*.ios.ascAppId`).
 */
export interface StoreApp {
  name: string;
  appStore: string;
  googlePlay: string;
  appStoreLive: boolean;
  googlePlayLive: boolean;
  /** At least one store is live: a QR code (svdy.com/app routes per platform) is worth showing. */
  live: boolean;
}

export const STORE_APPS: Record<ProductCode, StoreApp> = {
  qutime: {
    name: 'QuTime',
    appStore: 'https://apps.apple.com/app/id6792681404',
    googlePlay: 'https://play.google.com/store/apps/details?id=com.qutime.app',
    appStoreLive: true,
    googlePlayLive: true,
    live: true,
  },
  pureoa: {
    name: 'PureOA',
    appStore: 'https://apps.apple.com/app/id6792681902',
    googlePlay: 'https://play.google.com/store/apps/details?id=com.pureoa.app',
    appStoreLive: false,
    googlePlayLive: true,
    live: true,
  },
};

/**
 * What every QR code encodes: a page WE own (svdy.com/app), which sends a phone to
 * the right store for its platform. Never a store URL — one code then serves iPhone
 * and Android, and a printed or shared code keeps working if a listing URL changes
 * (docs/plans/APP_QR_LAUNCH_PLAN_2026-07-30.md).
 */
export const appPageUrl = (brand: ProductCode) => `https://svdy.com/app/?p=${brand}`;

/** The brands an organization uses, in a stable order. Falls back to the build brand. */
export function appBrandsFor(
  licensedProducts: readonly string[] | null | undefined,
  fallback: ProductCode,
): ProductCode[] {
  const brands = (['qutime', 'pureoa'] as ProductCode[]).filter((b) => licensedProducts?.includes(b));
  return brands.length ? brands : [fallback];
}
